import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import * as warehouseApi from "@/lib/api/warehouse";
import { useAuth } from "@/context/AuthContext";
import { canOperateInventory } from "@/lib/permissions";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { variantLabel } from "@/lib/format";
import { pickTaskBadgeStatus, pickTaskLabel } from "@/lib/warehouse-workflow";
import { fulfilmentWorkspacePath, formatShipTo } from "@/lib/fulfilment-workspace";

interface PickTaskRow {
  id: string;
  sales_order_id: string;
  location_id: string | null;
  status: string;
  assigned_to: string | null;
  started_at: string | null;
  completed_at: string | null;
  sales_orders?: { order_number: string; customer_id: string | null; shipping_address?: Record<string, unknown> | null } | null;
  locations?: { name: string } | null;
}

interface PickItemRow extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_required: number;
  qty_picked: number;
  products?: { name: string; sku: string | null; barcode: string | null } | null;
  variants?: { sku: string | null; barcode: string | null; option_values: Record<string, string> | null } | null;
}

function PickItemRowComponent({
  item,
  busy,
  onSet,
  onIncrement,
}: {
  item: PickItemRow;
  busy: boolean;
  onSet: (id: string, qty: number) => void;
  onIncrement: (id: string, qty: number) => void;
}) {
  const [inputVal, setInputVal] = useState(String(item.qty_picked));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setInputVal(String(item.qty_picked));
  }, [item.qty_picked, focused]);

  const commit = () => {
    const v = Number(inputVal);
    if (!Number.isNaN(v) && v !== item.qty_picked) onSet(item.id, v);
    setFocused(false);
  };

  const remaining = item.qty_required - item.qty_picked;
  const code = item.variants?.barcode ?? item.variants?.sku ?? item.products?.barcode ?? item.products?.sku;

  return (
    <tr className={`border-b border-slate-50 last:border-0 ${remaining === 0 ? "bg-emerald-50/40" : ""}`}>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-slate-800">
          {historicalProductName(item)}
          {(variantLabel(historicalVariantOptions(item)) || historicalSku(item)) && (
            <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(item)) || historicalSku(item)}</span>
          )}
        </p>
        {remaining === 0 && <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-wide">Picked</span>}
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 font-mono">{code ?? "—"}</td>
      <td className="px-4 py-3 text-right">
        <span className={`text-sm font-bold ${remaining === 0 ? "text-emerald-700" : "text-slate-900"}`}>{item.qty_picked}</span>
        <span className="text-slate-400 text-sm"> / {item.qty_required}</span>
      </td>
      <td className={`px-4 py-3 text-sm text-right font-medium ${remaining > 0 ? "text-amber-600" : "text-slate-300"}`}>{remaining}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onSet(item.id, item.qty_picked - 1)}
            disabled={busy || item.qty_picked === 0}
            aria-label={`Decrease quantity for ${item.products?.name ?? "item"}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
          >
            <i className="ri-subtract-line" aria-hidden="true" />
          </button>
          <button
            onClick={() => onIncrement(item.id, 1)}
            disabled={busy || remaining === 0}
            aria-label={`Pick one ${item.products?.name ?? "item"}`}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
          >
            <i className="ri-add-line text-lg" aria-hidden="true" />
          </button>
          <input
            type="number"
            min={0}
            max={item.qty_required}
            value={inputVal}
            onFocus={() => setFocused(true)}
            onChange={(e) => setInputVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
            }}
            aria-label={`Picked quantity for ${item.products?.name ?? "item"}`}
            className="w-16 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-right"
          />
        </div>
      </td>
    </tr>
  );
}

function PickItemCardComponent({
  item,
  busy,
  onSet,
  onIncrement,
}: {
  item: PickItemRow;
  busy: boolean;
  onSet: (id: string, qty: number) => void;
  onIncrement: (id: string, qty: number) => void;
}) {
  const [inputVal, setInputVal] = useState(String(item.qty_picked));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setInputVal(String(item.qty_picked));
  }, [item.qty_picked, focused]);

  const commit = () => {
    const v = Number(inputVal);
    if (!Number.isNaN(v) && v !== item.qty_picked) onSet(item.id, v);
    setFocused(false);
  };

  const remaining = item.qty_required - item.qty_picked;
  const code = item.variants?.barcode ?? item.variants?.sku ?? item.products?.barcode ?? item.products?.sku;

  return (
    <div className={`px-4 py-4 ${remaining === 0 ? "bg-emerald-50/40" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {historicalProductName(item)}
            {(variantLabel(historicalVariantOptions(item)) || historicalSku(item)) && (
              <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(item)) || historicalSku(item)}</span>
            )}
          </p>
          <p className="text-xs text-slate-400 font-mono mt-0.5">{code ?? "—"}</p>
          <p className="text-xs mt-1">
            <span className={`font-semibold ${remaining === 0 ? "text-emerald-700" : "text-slate-900"}`}>{item.qty_picked}</span>
            <span className="text-slate-400"> / {item.qty_required} picked</span>
            {remaining > 0 && <span className="text-amber-600 ml-1">· {remaining} left</span>}
          </p>
        </div>
        {remaining === 0 && <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-wide shrink-0">Picked</span>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={() => onSet(item.id, item.qty_picked - 1)}
          disabled={busy || item.qty_picked === 0}
          aria-label={`Decrease quantity for ${item.products?.name ?? "item"}`}
          className="h-12 w-12 shrink-0 flex items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
        >
          <i className="ri-subtract-line text-lg" aria-hidden="true" />
        </button>
        <input
          type="number"
          min={0}
          max={item.qty_required}
          value={inputVal}
          inputMode="numeric"
          onFocus={() => setFocused(true)}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
          aria-label={`Picked quantity for ${item.products?.name ?? "item"}`}
          className="h-12 w-16 px-2 bg-slate-50 border border-slate-200 rounded-xl text-base text-center"
        />
        <button
          onClick={() => onIncrement(item.id, 1)}
          disabled={busy || remaining === 0}
          aria-label={`Pick one ${item.products?.name ?? "item"}`}
          className="flex-1 h-12 flex items-center justify-center gap-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-base font-medium"
        >
          <i className="ri-add-line text-xl" aria-hidden="true" /> Pick
        </button>
      </div>
    </div>
  );
}

export default function PickSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { org, role, user } = useAuth();
  const queryClient = useQueryClient();
  const canPick = canOperateInventory(role);
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [done, setDone] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["pick-session", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [task, items] = await Promise.all([
        supabase
          .from("pick_tasks")
          .select("id, sales_order_id, location_id, status, assigned_to, started_at, completed_at, sales_orders(order_number, customer_id, shipping_address), locations(name)")
          .eq("org_id", org!.id)
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("pick_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, product_id, variant_id, qty_required, qty_picked, products(name, sku, barcode), variants(sku, barcode, option_values)")
          .eq("org_id", org!.id)
          .eq("pick_task_id", id)
          .order("created_at"),
      ]);
      const t = task.data
        ? {
            ...task.data,
            sales_orders: firstRelation(task.data.sales_orders),
            locations: firstRelation(task.data.locations),
          } as PickTaskRow
        : null;
      let customerName = "";
      if (t?.sales_orders?.customer_id) {
        const c = await supabase.from("customers").select("name").eq("id", t.sales_orders.customer_id).maybeSingle();
        customerName = c.data?.name ?? "";
      }
      return {
        task: t,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as PickItemRow[],
        customerName,
      };
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["pick-session", org?.id, id] });

  // Mark the task in-progress the moment a worker opens it.
  useEffect(() => {
    if (data?.task?.status === "pending" && canPick) {
      warehouseApi.startPickTask(id!).then(() => {
        refresh();
      }).catch((err) => {
        console.error("Failed to auto-start pick task", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.task?.status, id, canPick]);

  // Barcode/SKU → pick item id. A code that resolves to more than one distinct
  // item is stored as null (ambiguous) so we never silently guess between variants.
  const scanMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    (data?.items ?? []).forEach((it) => {
      if (!hasLiveCatalogueIdentity(it)) return;
      const codes = [it.variants?.barcode, it.variants?.sku, it.products?.barcode, it.products?.sku];
      codes.filter(Boolean).forEach((c) => {
        const key = String(c).trim().toLowerCase();
        if (map[key] === undefined) map[key] = it.id;
        else if (map[key] !== it.id) map[key] = null;
      });
    });
    return map;
  }, [data?.items]);

  const flash = (ok: boolean, text: string) => {
    setFeedback({ ok, text });
    window.setTimeout(() => setFeedback(null), 2500);
  };

  const setQty = async (pickItemId: string, qty: number) => {
    setBusy(true);
    try {
      await warehouseApi.recordPickItem(pickItemId, qty, false);
      refresh();
    } catch (e) {
      flash(false, (e as Error).message);
    }
    setBusy(false);
  };

  const increment = async (pickItemId: string, qty: number) => {
    setBusy(true);
    let ok = true;
    try {
      await warehouseApi.recordPickItem(pickItemId, qty, true);
      refresh();
    } catch (e) {
      flash(false, (e as Error).message);
      ok = false;
    }
    setBusy(false);
    return ok;
  };

  const handleScan = async () => {
    const code = scan.trim();
    if (!code) return;
    // Clear + refocus immediately (before any await) so rapid successive scans
    // never get concatenated while an RPC is in flight.
    setScan("");
    scanRef.current?.focus();
    const itemId = scanMap[code.toLowerCase()];
    if (itemId === undefined) {
      flash(false, "Barcode not found in this pick");
      return;
    }
    if (itemId === null) {
      flash(false, "This code matches multiple items — scan the item-specific barcode or SKU");
      return;
    }
    const ok = await increment(itemId, 1);
    if (ok) flash(true, "Picked +1");
  };

  const handleComplete = async () => {
    setBusy(true);
    try {
      await warehouseApi.completePickTask(id!);
    } catch (e) {
      setBusy(false);
      flash(false, (e as Error).message);
      return;
    }
    setBusy(false);
    setDone(true);
    queryClient.invalidateQueries({ queryKey: ["warehouse", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, data?.task?.sales_order_id] });
  };

  const handleReassign = async () => {
    setBusy(true);
    try {
      await warehouseApi.reassignPickTask(id!);
      refresh();
    } catch (e) {
      flash(false, (e as Error).message);
    }
    setBusy(false);
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.task) {
    return (
      <div>
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Picking</h1>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this pick task.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const task = data.task;
  const items = data.items;
  const totalRequired = items.reduce((s, it) => s + it.qty_required, 0);
  const totalPicked = items.reduce((s, it) => s + it.qty_picked, 0);
  const progress = totalRequired > 0 ? Math.round((totalPicked / totalRequired) * 100) : 0;
  const complete = totalRequired > 0 && totalPicked === totalRequired;
  const taskDone = done || task.status === "picked";

  return (
    <div className="max-w-3xl">
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate(fulfilmentWorkspacePath("picking"))} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back to Fulfilment
          </button>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-3">
            Pick <span className="font-mono">{task.sales_orders?.order_number ?? "—"}</span>
            <StatusBadge status={pickTaskBadgeStatus(taskDone ? "picked" : task.status)} customLabel={pickTaskLabel(taskDone ? "picked" : task.status)} />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {data.customerName || "—"} · Pick from <span className="font-medium text-slate-700">{task.locations?.name ?? "—"}</span>
            {" · "}Deliver to {formatShipTo(task.sales_orders?.shipping_address)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-slate-900">
            {totalPicked}<span className="text-slate-400 text-base font-normal"> / {totalRequired}</span>
          </p>
          <div className="w-40 h-2 bg-slate-100 rounded-full overflow-hidden mt-2 ml-auto">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {feedback && (
        <div role="status" aria-live="polite" className={`mb-4 px-3 py-2.5 rounded-lg text-sm flex items-center gap-2 ${feedback.ok ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-red-50 border border-red-100 text-red-700"}`}>
          <i className={feedback.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} aria-hidden="true" />
          {feedback.text}
        </div>
      )}

      {!taskDone && task.status === "in_progress" && task.assigned_to && task.assigned_to !== user?.id && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-amber-800">
            <i className="ri-user-line mr-1" aria-hidden="true" />
            This pick is assigned to another worker.
          </p>
          <button
            onClick={handleReassign}
            disabled={busy}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
          >
            Reassign to me
          </button>
        </div>
      )}

      {taskDone ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
            <i className="ri-check-line text-2xl" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Pick complete</h3>
          <p className="text-sm text-slate-500 mt-1 mb-5">
            {totalPicked} of {totalRequired} units confirmed. Stock has been fulfilled to the sales order.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => navigate(fulfilmentWorkspacePath("picking"))} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              Next task
            </button>
            <button onClick={() => navigate(`/sales-orders/${task.sales_order_id}`)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              View order
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <label htmlFor="pick-scan-input" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Scan barcode or SKU
            </label>
            <div className="flex items-center gap-2">
              <input
                id="pick-scan-input"
                ref={scanRef}
                autoFocus
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleScan();
                  }
                }}
                placeholder="Scan a barcode, then press Enter"
                className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-base font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              />
              <button
                onClick={handleScan}
                disabled={busy || !scan.trim()}
                className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-scan-line" /> Add
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">Most scanners act like a keyboard — just scan and the item increments automatically.</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Barcode / SKU</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Picked</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Remaining</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <PickItemRowComponent key={it.id} item={it} busy={busy} onSet={setQty} onIncrement={increment} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-slate-50">
              {items.map((it) => (
                <PickItemCardComponent key={it.id} item={it} busy={busy} onSet={setQty} onIncrement={increment} />
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
            <button
              onClick={async () => {
                try {
                  await warehouseApi.cancelPickTask(id!);
                  navigate(fulfilmentWorkspacePath("picking"));
                } catch (e) {
                  flash(false, (e as Error).message);
                }
              }}
              disabled={busy}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel pick
            </button>
            <button
              onClick={handleComplete}
              disabled={busy || totalPicked === 0}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 ${
                complete ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
              } disabled:opacity-50`}
            >
              <i className="ri-check-double-line" />
              {busy ? "Completing..." : complete ? "Complete pick" : "Complete partial pick"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}